import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Loader2 } from 'lucide-react';

import BudgetMembersHoursChart from '@/components/organisms/budget/BudgetMembersHoursChart';
import MembersSectionHeader from '@/components/molecules/budget/MembersSectionHeader';
import { Button } from '@/components/ui/button';
import { MEMBER_ROLE_LABELS } from '@/components/organisms/budget/budgetMember.constants';
import { budgetMemberService } from '@/services/budget-member.service';
import { workActivityService } from '@/services/work-activity.service';
import { BudgetMember } from '@/services/types/budget-members.types';
import { BudgetMemberHoursSeries, mapWorkActivityPerformanceToChartSeries } from '@/utils/budgetMembersChart';
import { useBudgetPermissions } from '@/hooks/useBudgetPermissions';
import { getCurrentUser } from '@/store/slices/auth';
import defaultUserAvatar from '@/assets/images/default-user.png';
import MobileAppBar from '@/components/molecules/MobileAppBar';

const mapApiRoleToLabel = (role: BudgetMember['role']): string => {
  if (role === 'OWNER') return MEMBER_ROLE_LABELS.owner;
  if (role === 'VALIDATOR') return MEMBER_ROLE_LABELS.validation;
  return MEMBER_ROLE_LABELS.staff;
};

const MembersPage = () => {
  const navigate = useNavigate();
  const { budgetId } = useParams<{ budgetId: string }>();
  const currentUser = useSelector(getCurrentUser);

  const [members, setMembers] = useState<BudgetMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [chartSeries, setChartSeries] = useState<BudgetMemberHoursSeries[]>([]);
  const [isChartLoading, setIsChartLoading] = useState(true);

  const { can } = useBudgetPermissions({
    budgetId,
    members
  });

  const canManageMembers = can('member:editDetails');

  useEffect(() => {
    const fetchPageData = async () => {
      if (!budgetId) {
        toast.error('Budget ID is missing');
        setIsLoading(false);
        setIsChartLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setIsChartLoading(true);

        const [membersResponse, performance] = await Promise.all([
          budgetMemberService.getMembers(budgetId),
          workActivityService.getWorkActivityPerformance(budgetId)
        ]);

        const membersData = membersResponse.data ?? [];
        setMembers(membersData);
        setChartSeries(mapWorkActivityPerformanceToChartSeries(performance, membersData));
      } catch (error) {
        console.error('Error fetching budget members page data:', error);
        toast.error('Failed to load members. Please try again.');
        setChartSeries([]);
      } finally {
        setIsLoading(false);
        setIsChartLoading(false);
      }
    };

    fetchPageData();
  }, [budgetId]);

  return (
    <>
      <MobileAppBar title="Manage Member" />
      <div className="flex-1 flex flex-col gap-8 p-4 md:p-0">
        <div className="border flex-1 rounded-[16px] p-4 pb-10 px-5 bg-white min-h-[320px]">
          {isChartLoading ? (
            <div className="flex items-center justify-center min-h-[280px] gap-2">
              <Loader2 className="w-5 h-5 animate-spin text-secondary" />
              <span className="text-sm text-secondary">Loading performance chart...</span>
            </div>
          ) : (
            <BudgetMembersHoursChart series={chartSeries} currentUserId={currentUser?.id ?? ''} />
          )}
        </div>

        <div className="md:border flex-1 flex flex-col rounded-[16px] md:p-4 md:bg-white space-y-3 md:space-y-6">
          <MembersSectionHeader memberCount={members.length} />

          <p className="text-sm font-bold">Team Members</p>
          <div className="flex-1 space-y-4">
            {isLoading ? (
              <p className="text-secondary text-sm">Loading members...</p>
            ) : members.length === 0 ? (
              <p className="text-secondary text-sm">No members found.</p>
            ) : (
              members.map((member) => {
                const isCurrentUser =
                  member.user_id === currentUser?.id ||
                  member.email.toLowerCase() === currentUser?.email?.toLowerCase();

                return (
                  <div
                    key={member._id}
                    className="flex items-center justify-between border-b pb-2.5 md:pb-0 md:border-b-0 last:border-b-0"
                  >
                    <div className="flex items-center gap-2">
                      <img
                        src={member.profile_pic_url || defaultUserAvatar}
                        alt={member.name}
                        className="size-8 rounded-full object-cover bg-lightGray"
                        onError={(event) => {
                          event.currentTarget.src = defaultUserAvatar;
                        }}
                      />
                      <div>
                        <p className="text-sm font-medium">
                          {member.name}
                          {isCurrentUser && <span className="text-secondary"> (You)</span>}
                        </p>
                        <p className="text-secondary text-xs">{member.email}</p>
                      </div>
                    </div>

                    <span className="text-primary text-xs font-medium min-w-[72px] text-right">
                      {mapApiRoleToLabel(member.role)}
                    </span>
                  </div>
                );
              })
            )}
          </div>

          {canManageMembers && (
            <footer className="flex justify-end py-6">
              <Button
                className="rounded-full w-full md:w-auto"
                variant="primary-reverse"
                type="button"
                onClick={() => navigate(`/budgets/${budgetId}/members/edit`)}
              >
                Manage Members
              </Button>
            </footer>
          )}
        </div>
      </div>
    </>
  );
};

export default MembersPage;
